// A share drawn as a grid of square cells (disk used): six rows, as many columns as fit, filled a column at a time
// from the left and each column from the bottom, so it reads like a bar made of pixels. The used cells are the
// accent, the rest a quiet stone; the grid is centred in its box, so it lines up with the 64px charts beside it.
// Sizes come from the box (ResizeObserver), cells land on whole pixels.

import { useLayoutEffect, useRef, useState } from 'react';

const GAP = 2;

/** rows: six in the charts' 64px slot; the machine card draws three in 28. */
export function PixelMeter({ share, label, className = '', rows: ROWS = 6 }: { share: number; label: string; className?: string; rows?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [[w, h], setBox] = useState<[number, number]>([0, 0]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setBox([Math.floor(e.contentRect.width), Math.floor(e.contentRect.height)]));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const s = Math.min(1, Math.max(0, share));
  const cell = Math.floor((h - (ROWS - 1) * GAP) / ROWS);
  const cols = cell > 0 ? Math.floor((w + GAP) / (cell + GAP)) : 0;
  const ox = Math.floor((w - (cols * cell + (cols - 1) * GAP)) / 2);
  const oy = Math.floor((h - (ROWS * cell + (ROWS - 1) * GAP)) / 2);
  const lit = Math.round(s * cols * ROWS);
  const cells = [];
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < ROWS; r++) {
      const on = c * ROWS + (ROWS - 1 - r) < lit;
      cells.push(<rect key={`${c}.${r}`} className={on ? 'pm-on' : undefined} x={ox + c * (cell + GAP)} y={oy + r * (cell + GAP)} width={cell} height={cell} rx="1" />);
    }
  }
  return (
    <div ref={ref} className={`pixel-meter ${className}`} role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(s * 100)}>
      {cols > 0 && (
        <svg width={w} height={h} aria-hidden="true" focusable="false">
          {cells}
        </svg>
      )}
    </div>
  );
}
